import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "../../styles/popupStyle.css";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { ReservePropertyEnum } from "../../../constants/NavbarList";
import {
  reservePropertyQuestionsTooltip,
  reserveReservationSettingsTooltip,
} from "../../../constants/ReserveTooltipInformation";
import { textStyle } from "../../styles/Style";
import {
  converttoDayjsTime,
  converttoStandardTime,
  propertyTime,
} from "../../../constants/PropertyInformation";
import { YesOrNo } from "../../../constants/YesOrNo";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import {
  RESERVE_RESERVATION_SETTINGS_CLOSE_SAME_DAY_OPTIONS,
  RESERVE_RESERVATION_SETTINGS_DEFAULTS,
  RESERVE_RESERVATION_SETTINGS_FIELD_ACCEPTS_ONLINE,
  RESERVE_RESERVATION_SETTINGS_FIELD_AUTO_SUGGEST_TABLES,
  RESERVE_RESERVATION_SETTINGS_FIELD_CLOSE_SAME_DAY_TIME,
  RESERVE_RESERVATION_SETTINGS_FIELD_CUT_OFF_MINUTES,
  RESERVE_RESERVATION_SETTINGS_FIELD_DAYS_IN_ADVANCE,
  RESERVE_RESERVATION_SETTINGS_FIELD_HELD_AFTER_RES_TIME,
  RESERVE_RESERVATION_SETTINGS_FIELD_MAX_PARTY_SIZE,
  RESERVE_RESERVATION_SETTINGS_FIELD_MIN_TIME_TO_CANCEL,
  RESERVE_RESERVATION_SETTINGS_FIELD_NO_SHOW_APPLIES_PARTY_SIZE,
  RESERVE_RESERVATION_SETTINGS_FIELD_NO_SHOW_FEE_AMOUNT,
  RESERVE_RESERVATION_SETTINGS_FIELD_NO_SHOW_FEE_SCHEDULE,
  RESERVE_RESERVATION_SETTINGS_FIELD_NO_SHOW_FEE_TYPE,
  RESERVE_RESERVATION_SETTINGS_FIELD_SLOTTING,
  RESERVE_RESERVATION_SETTINGS_NO_SHOW_FEE_SCHEDULE_OPTIONS,
  RESERVE_RESERVATION_SETTINGS_NO_SHOW_FEE_TYPE_OPTIONS,
  RESERVE_RESERVATION_SETTINGS_SLOTTING_OPTIONS,
} from "../../../constants/ReserveReservationSettingsFields";

const readReservationSetting = (
  row: Record<string, unknown> | null,
  legacyRow: Record<string, unknown> | null | undefined,
  field: string,
) => {
  const value = row?.[field] ?? legacyRow?.[field];
  return value != null ? String(value) : undefined;
};

const ReservePropertyQuestions = ({
  data,
  fieldValues,
  includeReservationSettings = false,
  reservationSettingsValues,
}: {
  data: any;
  fieldValues: any;
  includeReservationSettings?: boolean;
  reservationSettingsValues?: any;
}) => {
  const [primaryId, setPrimaryId] = useState<number>(1);
  const [acceptsWebReservations, setAcceptsWebReservations] =
    useState<boolean>(true);
  const [closeSameDayTime, setCloseSameDayTime] = useState<any>(
    propertyTime.CHECK_IN,
  );
  const [requiredPartyField, setRequiredPartyField] = useState<string>("phone");
  const [showCheckedInInTimeline, setShowCheckedInInTimeline] =
    useState<boolean>(true);
  const [acceptsOnlineReservations, setAcceptsOnlineReservations] = useState<
    string | null
  >(
    RESERVE_RESERVATION_SETTINGS_DEFAULTS[
      RESERVE_RESERVATION_SETTINGS_FIELD_ACCEPTS_ONLINE
    ],
  );
  const [maxPartySize, setMaxPartySize] = useState<string | null>(
    RESERVE_RESERVATION_SETTINGS_DEFAULTS[
      RESERVE_RESERVATION_SETTINGS_FIELD_MAX_PARTY_SIZE
    ],
  );
  const [daysInAdvanceForReservations, setDaysInAdvanceForReservations] =
    useState<string | null>(
      RESERVE_RESERVATION_SETTINGS_DEFAULTS[
        RESERVE_RESERVATION_SETTINGS_FIELD_DAYS_IN_ADVANCE
      ],
    );
  const [reservationCutOffMinutes, setReservationCutOffMinutes] = useState<
    string | null
  >(
    RESERVE_RESERVATION_SETTINGS_DEFAULTS[
      RESERVE_RESERVATION_SETTINGS_FIELD_CUT_OFF_MINUTES
    ],
  );
  const [closeSameDayReservationsTime, setCloseSameDayReservationsTime] =
    useState<string | null>(
      RESERVE_RESERVATION_SETTINGS_DEFAULTS[
        RESERVE_RESERVATION_SETTINGS_FIELD_CLOSE_SAME_DAY_TIME
      ],
    );
  const [minimumTimeToCancelReservation, setMinimumTimeToCancelReservation] =
    useState<string | null>(
      RESERVE_RESERVATION_SETTINGS_DEFAULTS[
        RESERVE_RESERVATION_SETTINGS_FIELD_MIN_TIME_TO_CANCEL
      ],
    );
  const [automaticOrManualSlotting, setAutomaticOrManualSlotting] = useState<
    string | null
  >(
    RESERVE_RESERVATION_SETTINGS_DEFAULTS[
      RESERVE_RESERVATION_SETTINGS_FIELD_SLOTTING
    ],
  );
  const [reservationHeldAfterResTime, setReservationHeldAfterResTime] =
    useState<string | null>(
      RESERVE_RESERVATION_SETTINGS_DEFAULTS[
        RESERVE_RESERVATION_SETTINGS_FIELD_HELD_AFTER_RES_TIME
      ],
    );
  const [autoSuggestTables, setAutoSuggestTables] = useState<string | null>(
    RESERVE_RESERVATION_SETTINGS_DEFAULTS[
      RESERVE_RESERVATION_SETTINGS_FIELD_AUTO_SUGGEST_TABLES
    ],
  );
  const [noShowFeeAmount, setNoShowFeeAmount] = useState<string | null>(
    RESERVE_RESERVATION_SETTINGS_DEFAULTS[
      RESERVE_RESERVATION_SETTINGS_FIELD_NO_SHOW_FEE_AMOUNT
    ],
  );
  const [noShowFeeType, setNoShowFeeType] = useState<string | null>(
    RESERVE_RESERVATION_SETTINGS_DEFAULTS[
      RESERVE_RESERVATION_SETTINGS_FIELD_NO_SHOW_FEE_TYPE
    ],
  );
  const [noShowFeeSchedule, setNoShowFeeSchedule] = useState<string | null>(
    RESERVE_RESERVATION_SETTINGS_DEFAULTS[
      RESERVE_RESERVATION_SETTINGS_FIELD_NO_SHOW_FEE_SCHEDULE
    ],
  );
  const [noShowAppliesToPartySize, setNoShowAppliesToPartySize] = useState<
    string | null
  >(
    RESERVE_RESERVATION_SETTINGS_DEFAULTS[
      RESERVE_RESERVATION_SETTINGS_FIELD_NO_SHOW_APPLIES_PARTY_SIZE
    ],
  );
  const [maxPartySizeError, setMaxPartySizeError] = useState<boolean>(false);

  useEffect(() => {
    if (fieldValues == null) return;
    const row: Record<string, unknown> | null = Array.isArray(fieldValues)
      ? fieldValues[0] != null && typeof fieldValues[0] === "object"
        ? (fieldValues[0] as Record<string, unknown>)
        : null
      : typeof fieldValues === "object"
        ? (fieldValues as Record<string, unknown>)
        : null;
    const legacyRow: Record<string, unknown> | null | undefined =
      Array.isArray(reservationSettingsValues) &&
      reservationSettingsValues[0] != null &&
      typeof reservationSettingsValues[0] === "object"
        ? (reservationSettingsValues[0] as Record<string, unknown>)
        : null;
    if (!row && !legacyRow) return;

    const sourceRow = row ?? legacyRow;
    if (!sourceRow) return;

    const idRaw = sourceRow.id;
    setPrimaryId(typeof idRaw === "number" ? idRaw : Number(idRaw) || 1);
    setAcceptsWebReservations(
      typeof row?.acceptsWebReservations === "boolean"
        ? row.acceptsWebReservations
        : true,
    );
    const closeAt = row?.closeSameDayWebReservationAt;
    setCloseSameDayTime(
      closeAt ? converttoDayjsTime(closeAt) : propertyTime.CHECK_IN,
    );
    setRequiredPartyField(
      row?.requiredPartyField === "email" ? "email" : "phone",
    );
    setShowCheckedInInTimeline(
      typeof row?.showCheckedInReservationsInTimelineView === "boolean"
        ? row.showCheckedInReservationsInTimelineView
        : true,
    );

    if (includeReservationSettings) {
      setAcceptsOnlineReservations(
        readReservationSetting(
          row,
          legacyRow,
          RESERVE_RESERVATION_SETTINGS_FIELD_ACCEPTS_ONLINE,
        ) ??
          RESERVE_RESERVATION_SETTINGS_DEFAULTS[
            RESERVE_RESERVATION_SETTINGS_FIELD_ACCEPTS_ONLINE
          ],
      );
      setMaxPartySize(
        readReservationSetting(
          row,
          legacyRow,
          RESERVE_RESERVATION_SETTINGS_FIELD_MAX_PARTY_SIZE,
        ) ??
          RESERVE_RESERVATION_SETTINGS_DEFAULTS[
            RESERVE_RESERVATION_SETTINGS_FIELD_MAX_PARTY_SIZE
          ],
      );
      setDaysInAdvanceForReservations(
        readReservationSetting(
          row,
          legacyRow,
          RESERVE_RESERVATION_SETTINGS_FIELD_DAYS_IN_ADVANCE,
        ) ??
          RESERVE_RESERVATION_SETTINGS_DEFAULTS[
            RESERVE_RESERVATION_SETTINGS_FIELD_DAYS_IN_ADVANCE
          ],
      );
      setReservationCutOffMinutes(
        readReservationSetting(
          row,
          legacyRow,
          RESERVE_RESERVATION_SETTINGS_FIELD_CUT_OFF_MINUTES,
        ) ??
          RESERVE_RESERVATION_SETTINGS_DEFAULTS[
            RESERVE_RESERVATION_SETTINGS_FIELD_CUT_OFF_MINUTES
          ],
      );
      setCloseSameDayReservationsTime(
        readReservationSetting(
          row,
          legacyRow,
          RESERVE_RESERVATION_SETTINGS_FIELD_CLOSE_SAME_DAY_TIME,
        ) ??
          RESERVE_RESERVATION_SETTINGS_DEFAULTS[
            RESERVE_RESERVATION_SETTINGS_FIELD_CLOSE_SAME_DAY_TIME
          ],
      );
      setMinimumTimeToCancelReservation(
        readReservationSetting(
          row,
          legacyRow,
          RESERVE_RESERVATION_SETTINGS_FIELD_MIN_TIME_TO_CANCEL,
        ) ??
          RESERVE_RESERVATION_SETTINGS_DEFAULTS[
            RESERVE_RESERVATION_SETTINGS_FIELD_MIN_TIME_TO_CANCEL
          ],
      );
      setAutomaticOrManualSlotting(
        readReservationSetting(
          row,
          legacyRow,
          RESERVE_RESERVATION_SETTINGS_FIELD_SLOTTING,
        ) ??
          RESERVE_RESERVATION_SETTINGS_DEFAULTS[
            RESERVE_RESERVATION_SETTINGS_FIELD_SLOTTING
          ],
      );
      setReservationHeldAfterResTime(
        readReservationSetting(
          row,
          legacyRow,
          RESERVE_RESERVATION_SETTINGS_FIELD_HELD_AFTER_RES_TIME,
        ) ??
          RESERVE_RESERVATION_SETTINGS_DEFAULTS[
            RESERVE_RESERVATION_SETTINGS_FIELD_HELD_AFTER_RES_TIME
          ],
      );
      setAutoSuggestTables(
        readReservationSetting(
          row,
          legacyRow,
          RESERVE_RESERVATION_SETTINGS_FIELD_AUTO_SUGGEST_TABLES,
        ) ??
          RESERVE_RESERVATION_SETTINGS_DEFAULTS[
            RESERVE_RESERVATION_SETTINGS_FIELD_AUTO_SUGGEST_TABLES
          ],
      );
      setNoShowFeeAmount(
        readReservationSetting(
          row,
          legacyRow,
          RESERVE_RESERVATION_SETTINGS_FIELD_NO_SHOW_FEE_AMOUNT,
        ) ?? "",
      );
      setNoShowFeeType(
        readReservationSetting(
          row,
          legacyRow,
          RESERVE_RESERVATION_SETTINGS_FIELD_NO_SHOW_FEE_TYPE,
        ) ?? "",
      );
      setNoShowFeeSchedule(
        readReservationSetting(
          row,
          legacyRow,
          RESERVE_RESERVATION_SETTINGS_FIELD_NO_SHOW_FEE_SCHEDULE,
        ) ?? "",
      );
      setNoShowAppliesToPartySize(
        readReservationSetting(
          row,
          legacyRow,
          RESERVE_RESERVATION_SETTINGS_FIELD_NO_SHOW_APPLIES_PARTY_SIZE,
        ) ?? "",
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate once from initial fieldValues
  }, []);

  const validateInput = () => {
    if (!includeReservationSettings) return;
    setMaxPartySizeError(false);
    if (
      maxPartySize &&
      !validateString(
        maxPartySize,
        validationLength.departmentName,
        validationType.OTHER,
      )
    ) {
      setMaxPartySizeError(true);
    }
  };

  useEffect(() => {
    validateInput();
    const payload: Record<string, unknown> = {
      id: primaryId,
      acceptsWebReservations,
      closeSameDayWebReservationAt: closeSameDayTime
        ? converttoStandardTime(closeSameDayTime)
        : null,
      requiredPartyField,
      showCheckedInReservationsInTimelineView: showCheckedInInTimeline,
    };

    if (includeReservationSettings) {
      payload[RESERVE_RESERVATION_SETTINGS_FIELD_ACCEPTS_ONLINE] =
        acceptsOnlineReservations;
      payload[RESERVE_RESERVATION_SETTINGS_FIELD_MAX_PARTY_SIZE] = maxPartySize;
      payload[RESERVE_RESERVATION_SETTINGS_FIELD_DAYS_IN_ADVANCE] =
        daysInAdvanceForReservations;
      payload[RESERVE_RESERVATION_SETTINGS_FIELD_CUT_OFF_MINUTES] =
        reservationCutOffMinutes;
      payload[RESERVE_RESERVATION_SETTINGS_FIELD_CLOSE_SAME_DAY_TIME] =
        closeSameDayReservationsTime;
      payload[RESERVE_RESERVATION_SETTINGS_FIELD_MIN_TIME_TO_CANCEL] =
        minimumTimeToCancelReservation;
      payload[RESERVE_RESERVATION_SETTINGS_FIELD_SLOTTING] =
        automaticOrManualSlotting;
      payload[RESERVE_RESERVATION_SETTINGS_FIELD_HELD_AFTER_RES_TIME] =
        reservationHeldAfterResTime;
      payload[RESERVE_RESERVATION_SETTINGS_FIELD_AUTO_SUGGEST_TABLES] =
        autoSuggestTables;
      payload[RESERVE_RESERVATION_SETTINGS_FIELD_NO_SHOW_FEE_AMOUNT] =
        noShowFeeAmount;
      payload[RESERVE_RESERVATION_SETTINGS_FIELD_NO_SHOW_FEE_TYPE] =
        noShowFeeType;
      payload[RESERVE_RESERVATION_SETTINGS_FIELD_NO_SHOW_FEE_SCHEDULE] =
        noShowFeeSchedule;
      payload[RESERVE_RESERVATION_SETTINGS_FIELD_NO_SHOW_APPLIES_PARTY_SIZE] =
        noShowAppliesToPartySize;
    }

    data([payload]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- parent passes unstable callback
  }, [
    primaryId,
    acceptsWebReservations,
    closeSameDayTime,
    requiredPartyField,
    showCheckedInInTimeline,
    includeReservationSettings,
    acceptsOnlineReservations,
    maxPartySize,
    daysInAdvanceForReservations,
    reservationCutOffMinutes,
    closeSameDayReservationsTime,
    minimumTimeToCancelReservation,
    automaticOrManualSlotting,
    reservationHeldAfterResTime,
    autoSuggestTables,
    noShowFeeAmount,
    noShowFeeType,
    noShowFeeSchedule,
    noShowAppliesToPartySize,
  ]);

  return (
    <Stack>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Typography variant="h6" component="div" sx={textStyle}>
          {ReservePropertyEnum.propertyQuestions}
          <Tooltip
            title={tooltipComponent(reservePropertyQuestionsTooltip.header)}
          >
            <InfoIcon fontSize="small" />
          </Tooltip>
        </Typography>

        <Stack
          className="form-checkbox-row form-two-col-row"
          direction="row"
          width="100%"
          gap="20px"
          flexWrap="wrap"
        >
          <Stack direction="column" width="100%" gap="5px">
            <FormLabel component="legend">
              Accepts Web Reservations
              <Tooltip
                title={tooltipComponent(
                  reservePropertyQuestionsTooltip.acceptsWebReservations,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <FormControlLabel
              control={
                <Switch
                  checked={acceptsWebReservations}
                  onChange={(_, v) => setAcceptsWebReservations(v)}
                  color="primary"
                />
              }
              label={acceptsWebReservations ? "Yes" : "No"}
              sx={{ alignSelf: "flex-start" }}
            />
          </Stack>

          <Stack direction="column" width="100%" gap="5px">
            <FormLabel component="legend">
              Close Same Day Web Reservation At
              <Tooltip
                title={tooltipComponent(
                  reservePropertyQuestionsTooltip.closeSameDayWebReservationAt,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker
                label="Time"
                value={closeSameDayTime}
                onChange={(v) => setCloseSameDayTime(v)}
                disabled={!acceptsWebReservations}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined",
                    sx: { width: "100%" },
                  },
                }}
              />
            </LocalizationProvider>
          </Stack>

          <Stack direction="column" width="100%" gap="5px">
            <FormLabel component="legend">
              Required Party Field
              <Tooltip
                title={tooltipComponent(
                  reservePropertyQuestionsTooltip.requiredPartyField,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="reserve-required-party-field">
                Phone or email (Host Application)
              </InputLabel>
              <Select
                labelId="reserve-required-party-field"
                label="Phone or email (Host Application)"
                value={requiredPartyField ?? "phone"}
                onChange={(e) => setRequiredPartyField(String(e.target.value))}
              >
                <MenuItem value="phone">Phone</MenuItem>
                <MenuItem value="email">Email</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <Stack direction="column" width="100%" gap="5px">
            <FormLabel component="legend">
              Show checked-in reservations in &quot;Timeline&quot; View
              <Tooltip
                title={tooltipComponent(
                  reservePropertyQuestionsTooltip.showCheckedInReservationsInTimelineView,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <FormControlLabel
              control={
                <Switch
                  checked={showCheckedInInTimeline}
                  onChange={(_, v) => setShowCheckedInInTimeline(v)}
                  color="primary"
                />
              }
              label={showCheckedInInTimeline ? "Yes" : "No"}
              sx={{ alignSelf: "flex-start" }}
            />
          </Stack>
        </Stack>

        {includeReservationSettings && (
          <Stack direction="column" width="100%" gap="20px" mt={2}>
            <FormLabel component="legend">
              {RESERVE_RESERVATION_SETTINGS_FIELD_ACCEPTS_ONLINE}
              <Tooltip
                title={tooltipComponent(
                  reserveReservationSettingsTooltip.acceptsOnlineReservations,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <Select
              id="acceptsOnlineReservations"
              value={acceptsOnlineReservations ?? ""}
              onChange={(e) => setAcceptsOnlineReservations(e.target.value)}
              sx={{ width: "100%" }}
            >
              {YesOrNo.map((type, index) => (
                <MenuItem value={type} key={index}>
                  {type}
                </MenuItem>
              ))}
            </Select>

            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                {RESERVE_RESERVATION_SETTINGS_FIELD_MAX_PARTY_SIZE}
                <Tooltip
                  title={tooltipComponent(
                    reserveReservationSettingsTooltip.maxPartySize,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="maxPartySize"
                variant="outlined"
                placeholder={RESERVE_RESERVATION_SETTINGS_FIELD_MAX_PARTY_SIZE}
                value={maxPartySize ?? ""}
                helperText={
                  maxPartySizeError ? validationMessages.departmentName : ""
                }
                error={maxPartySizeError}
                onChange={(e) => {
                  setMaxPartySize(e.target.value);
                  setMaxPartySizeError(false);
                }}
                sx={{ width: "100%" }}
              />
            </Stack>

            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                {RESERVE_RESERVATION_SETTINGS_FIELD_DAYS_IN_ADVANCE}
                <Tooltip
                  title={tooltipComponent(
                    reserveReservationSettingsTooltip.daysInAdvance,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="daysInAdvanceForReservations"
                variant="outlined"
                placeholder={RESERVE_RESERVATION_SETTINGS_FIELD_DAYS_IN_ADVANCE}
                value={daysInAdvanceForReservations ?? ""}
                sx={{ width: "100%" }}
                onChange={(e) =>
                  setDaysInAdvanceForReservations(e.target.value)
                }
              />
            </Stack>

            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                {RESERVE_RESERVATION_SETTINGS_FIELD_CUT_OFF_MINUTES}
                <Tooltip
                  title={tooltipComponent(
                    reserveReservationSettingsTooltip.reservationCutOffMinutes,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="reservationCutOffMinutes"
                variant="outlined"
                placeholder={RESERVE_RESERVATION_SETTINGS_FIELD_CUT_OFF_MINUTES}
                value={reservationCutOffMinutes ?? ""}
                sx={{ width: "100%" }}
                onChange={(e) => setReservationCutOffMinutes(e.target.value)}
              />
            </Stack>

            <FormLabel component="legend">
              {RESERVE_RESERVATION_SETTINGS_FIELD_CLOSE_SAME_DAY_TIME}
              <Tooltip
                title={tooltipComponent(
                  reserveReservationSettingsTooltip.closeSameDayReservationsTime,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <Select
              id="closeSameDayReservationsTime"
              value={closeSameDayReservationsTime ?? ""}
              onChange={(e) => setCloseSameDayReservationsTime(e.target.value)}
              sx={{ width: "100%" }}
            >
              {RESERVE_RESERVATION_SETTINGS_CLOSE_SAME_DAY_OPTIONS.map(
                (type, index) => (
                  <MenuItem value={type} key={index}>
                    {type}
                  </MenuItem>
                ),
              )}
            </Select>

            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                {RESERVE_RESERVATION_SETTINGS_FIELD_MIN_TIME_TO_CANCEL}
                <Tooltip
                  title={tooltipComponent(
                    reserveReservationSettingsTooltip.minimumTimeToCancelReservation,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="minimumTimeToCancelReservation"
                variant="outlined"
                placeholder={
                  RESERVE_RESERVATION_SETTINGS_FIELD_MIN_TIME_TO_CANCEL
                }
                value={minimumTimeToCancelReservation ?? ""}
                sx={{ width: "100%" }}
                onChange={(e) =>
                  setMinimumTimeToCancelReservation(e.target.value)
                }
              />
            </Stack>

            <FormLabel component="legend">
              {RESERVE_RESERVATION_SETTINGS_FIELD_SLOTTING}
              <Tooltip
                title={tooltipComponent(
                  reserveReservationSettingsTooltip.automaticOrManualSlotting,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <Select
              id="automaticOrManualSlotting"
              value={automaticOrManualSlotting ?? ""}
              onChange={(e) => setAutomaticOrManualSlotting(e.target.value)}
              sx={{ width: "100%" }}
            >
              {RESERVE_RESERVATION_SETTINGS_SLOTTING_OPTIONS.map(
                (type, index) => (
                  <MenuItem value={type} key={index}>
                    {type}
                  </MenuItem>
                ),
              )}
            </Select>

            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                {RESERVE_RESERVATION_SETTINGS_FIELD_HELD_AFTER_RES_TIME}
                <Tooltip
                  title={tooltipComponent(
                    reserveReservationSettingsTooltip.reservationHeldAfterResTime,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="reservationHeldAfterResTime"
                variant="outlined"
                placeholder={
                  RESERVE_RESERVATION_SETTINGS_FIELD_HELD_AFTER_RES_TIME
                }
                value={reservationHeldAfterResTime ?? ""}
                sx={{ width: "100%" }}
                onChange={(e) => setReservationHeldAfterResTime(e.target.value)}
              />
            </Stack>

            <FormLabel component="legend">
              {RESERVE_RESERVATION_SETTINGS_FIELD_AUTO_SUGGEST_TABLES}
              <Tooltip
                title={tooltipComponent(
                  reserveReservationSettingsTooltip.autoSuggestTables,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <Select
              id="autoSuggestTables"
              value={autoSuggestTables ?? ""}
              onChange={(e) => setAutoSuggestTables(e.target.value)}
              sx={{ width: "100%" }}
            >
              {YesOrNo.map((type, index) => (
                <MenuItem value={type} key={index}>
                  {type}
                </MenuItem>
              ))}
            </Select>

            <Typography variant="subtitle1" sx={textStyle}>
              No show Fees policy
              <Tooltip
                title={tooltipComponent(
                  reserveReservationSettingsTooltip.noShowFeesPolicy,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>

            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                {RESERVE_RESERVATION_SETTINGS_FIELD_NO_SHOW_FEE_AMOUNT}
              </FormLabel>
              <TextField
                id="noShowFeeAmount"
                variant="outlined"
                placeholder={
                  RESERVE_RESERVATION_SETTINGS_FIELD_NO_SHOW_FEE_AMOUNT
                }
                value={noShowFeeAmount ?? ""}
                sx={{ width: "100%" }}
                onChange={(e) => setNoShowFeeAmount(e.target.value)}
              />
            </Stack>

            <FormLabel component="legend">
              {RESERVE_RESERVATION_SETTINGS_FIELD_NO_SHOW_FEE_TYPE}
            </FormLabel>
            <Select
              id="noShowFeeType"
              value={noShowFeeType ?? ""}
              onChange={(e) => setNoShowFeeType(e.target.value)}
              displayEmpty
              sx={{ width: "100%" }}
            >
              <MenuItem value="">
                <em>Select…</em>
              </MenuItem>
              {RESERVE_RESERVATION_SETTINGS_NO_SHOW_FEE_TYPE_OPTIONS.map(
                (type, index) => (
                  <MenuItem value={type} key={index}>
                    {type}
                  </MenuItem>
                ),
              )}
            </Select>

            <FormLabel component="legend">
              {RESERVE_RESERVATION_SETTINGS_FIELD_NO_SHOW_FEE_SCHEDULE}
            </FormLabel>
            <Select
              id="noShowFeeSchedule"
              value={noShowFeeSchedule ?? ""}
              onChange={(e) => setNoShowFeeSchedule(e.target.value)}
              displayEmpty
              sx={{ width: "100%" }}
            >
              <MenuItem value="">
                <em>Select…</em>
              </MenuItem>
              {RESERVE_RESERVATION_SETTINGS_NO_SHOW_FEE_SCHEDULE_OPTIONS.map(
                (type, index) => (
                  <MenuItem value={type} key={index}>
                    {type}
                  </MenuItem>
                ),
              )}
            </Select>

            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                {RESERVE_RESERVATION_SETTINGS_FIELD_NO_SHOW_APPLIES_PARTY_SIZE}
              </FormLabel>
              <TextField
                id="noShowAppliesToPartySize"
                variant="outlined"
                placeholder={
                  RESERVE_RESERVATION_SETTINGS_FIELD_NO_SHOW_APPLIES_PARTY_SIZE
                }
                value={noShowAppliesToPartySize ?? ""}
                sx={{ width: "100%" }}
                onChange={(e) => setNoShowAppliesToPartySize(e.target.value)}
              />
            </Stack>
          </Stack>
        )}
      </Paper>
    </Stack>
  );
};

export default ReservePropertyQuestions;
